// R11P GEOLOGICAL P0.5-P9A — Portal status registry + public-disclosure invariant.
//
// ONE place that maps a backend status string to a user-facing { label, tone }.
// Rules enforced here (and by the P9A tests):
//   - a negative/terminal status is NEVER rendered with a positive tone;
//   - an unknown status is NEUTRAL (never positive, never invented);
//   - the same status resolves to the same label + tone everywhere.
// Backend vocabulary is NOT changed — this only governs presentation.

export type StatusTone = 'positive' | 'neutral' | 'info' | 'warning' | 'negative';

export interface StatusView {
  label: string;
  tone: StatusTone;
}

const UNKNOWN: StatusView = { label: 'Unknown', tone: 'neutral' };

// Content publication lifecycle (contentService vocabulary).
const CONTENT: Record<string, StatusView> = {
  draft: { label: 'Draft', tone: 'neutral' },
  in_review: { label: 'In review', tone: 'info' },
  approved: { label: 'Approved', tone: 'info' }, // approved ≠ public (see isPubliclyDisclosed)
  published: { label: 'Published', tone: 'positive' },
  archived: { label: 'Archived', tone: 'neutral' },
};

// Investor application workflow (geo-api application vocabulary + API-normalized
// aliases). P9D: an `approved` application is an application decision — NEVER a
// permit/licence/certificate issuance (those are a separate backend action).
const APPLICATION: Record<string, StatusView> = {
  draft: { label: 'Draft', tone: 'neutral' },
  submitted: { label: 'Submitted', tone: 'info' },
  under_validation: { label: 'Under validation', tone: 'info' },
  under_review: { label: 'Under review', tone: 'info' },
  'in-review': { label: 'In review', tone: 'info' }, // API-normalized alias
  returned: { label: 'Returned for correction', tone: 'warning' },
  requires_revision: { label: 'Revision requested', tone: 'warning' },
  resubmitted: { label: 'Resubmitted', tone: 'info' },
  approved: { label: 'Approved', tone: 'positive' },
  rejected: { label: 'Rejected', tone: 'negative' },
  withdrawn: { label: 'Withdrawn', tone: 'neutral' },
};

// GIS classification + publication/access (gisService vocabulary).
const GIS: Record<string, StatusView> = {
  public: { label: 'Public', tone: 'positive' },
  restricted: { label: 'Restricted', tone: 'warning' },
  confidential: { label: 'Confidential', tone: 'negative' },
  draft: { label: 'Draft', tone: 'neutral' },
  pending: { label: 'Pending', tone: 'info' },
  published: { label: 'Published', tone: 'positive' },
  unpublished: { label: 'Not published', tone: 'neutral' },
  locked: { label: 'Restricted — access required', tone: 'warning' },
};

const DOMAINS = { content: CONTENT, application: APPLICATION, gis: GIS } as const;
export type StatusDomain = keyof typeof DOMAINS;

/** Resolve a backend status string to a presentation view. Unknown → neutral. */
export function statusView(domain: StatusDomain, status: string | null | undefined): StatusView {
  if (!status) return UNKNOWN;
  const key = String(status).toLowerCase().trim();
  return DOMAINS[domain][key] ?? { label: status, tone: 'neutral' };
}

/**
 * HARD GOVERNANCE INVARIANT (P9A §4/§26):
 *   GEOLOGICAL APPROVAL ≠ INTERNAL GIS PUBLICATION ≠ PUBLIC DISCLOSURE
 *
 * The ONLY signal of public disclosure is an explicit server field on the
 * resource (publicDisclosure === true, or an equivalent server-set boolean such
 * as isPublic / publiclyDisclosed). Approval, internal GIS publication, access
 * grants and classification values MUST NEVER imply public disclosure. This
 * function (and its P9A test) exists so any future change that infers
 * `approved → public` or `publishedToGis → public` fails a retained test.
 */
export function isPubliclyDisclosed(resource: unknown): boolean {
  if (!resource || typeof resource !== 'object') return false;
  const r = resource as Record<string, unknown>;
  return r.publicDisclosure === true || r.publiclyDisclosed === true || r.isPublic === true;
}
